import React, { useEffect, useState } from 'react';
import { debounce, trim, isEmpty } from 'lodash';
import { Col, Row, Tooltip, Spin } from 'antd';

import Modal from "../../components/uiComponent/modal/index";
import Input from '../../components/uiComponent/inputs/labelInput';
import Button from "../../components/uiComponent/button/index";
import AsyncSearch from '../../components/uiComponent/asyncSelect/index.js';
import InventoryWrapper from './style';
import { GetSkusList, MapSkus, clearField } from '../../slices/3-layer-products';
import { useDispatch, useSelector } from 'react-redux';
import { copyToClip } from '../../utils/utils';
import { STORAGE_IMAGE_URL } from '../../constants/storage';

const ProductImg = `${STORAGE_IMAGE_URL}/newImages/nestle.svg`;
const copyIcon = `${STORAGE_IMAGE_URL}/newImages/copy-white-icon.svg`;
const deleteIcon = `${STORAGE_IMAGE_URL}/newImages/delete-outlined.svg`;
const noData = `${STORAGE_IMAGE_URL}/newImages/no-data-icon.svg`;

const MapSkuModal = ({ open, onHide, onSave, rowIndex, expandRow, handleShowMappingAlert }) => {
  const dispatch = useDispatch();

  const [addedSkus, setAddedSkus] = useState([]);
  const [selectedSku, setSelectedSku] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [page, setPage] = useState({ pageSize: 50, pageNumber: 1 });
  const [filters, setFilters] = useState({ search: '' });

  const {
    products,
    skusList,
    mappingSkus,
    fetchingSkusList,
    totalSkusListCount
  } = useSelector((state) => state.threeLayerProducts || {});

  useEffect(() => {
    dispatch(GetSkusList({
      product_id: products[rowIndex].id,
      page,
      filters
    }));

    return () => {
      dispatch(clearField({
        field: 'skusList'
      }));
    }
  }, []);

  const handleAddSku = () => {
    if (selectedSku && quantity) {
      const newSku = {
        id: selectedSku.value,
        image: selectedSku.image || ProductImg,
        title: selectedSku.label,
        asin: selectedSku.identifier1,
        seller_sku: selectedSku.identifier2,
        qty: quantity
      };

      setAddedSkus(prev => {
        const existingItemIndex = prev.findIndex(item => item.seller_sku === newSku.seller_sku);

        if (existingItemIndex !== -1) {
          const updatedSkus = [...prev];
          updatedSkus[existingItemIndex].qty = Number(updatedSkus[existingItemIndex].qty) + Number(newSku.qty);
          return updatedSkus;
        }
        return [newSku, ...prev];
      });
      setSelectedSku(null);
      setQuantity("");
      setFilters({ search: '' });
      dispatch(GetSkusList({
        product_id: products[rowIndex].id,
        page
      }));
    }
  };

  const handleDeleteSku = (index) => {
    setAddedSkus((prev) => prev.filter((_, i) => i !== index));
  };


  const handleSkuSearch = debounce((value, callback) => {
    if (trim(value) || filters.search) {
      setFilters({ search: trim(value) });
      setPage(page => ({ ...page, pageNumber: 1 }));

      dispatch(GetSkusList({
        product_id: products[rowIndex].id,
        page: { ...page, pageNumber: 1 },
        filters: { search: trim(value) },
      }))
        .then(({ payload }) => {
          const { data } = payload?.data || {};
          callback?.(getMappedSkusList(data));
        });
    }
  }, 800);

  const handleScroll = () => {
    if (
      !fetchingSkusList &&
      totalSkusListCount !== skusList.length
    ) {
      const newPage = { ...page, pageNumber: page.pageNumber + 1 };
      setPage(newPage);

      dispatch(GetSkusList({
        product_id: products[rowIndex].id,
        page: newPage,
        filters,
        saveData: true
      }));
    }
  };

  const getMappedSkusList = (skusList) => {
    return skusList?.map(({ id: value, image_url: image, item_name: label, sku, vendorasin }) => ({
      value,
      image,
      label,
      identifier1: vendorasin?.asin,
      identifier2: sku,
    })) || [];
  };

  return (
    <Modal
      open={open}
      onHide={onHide}
      size="lg"
      closeText="Cancel"
      saveText="Confirm"
      header={true}
      closeButton={true}
      onClose={onHide}
      onSave={() => {
        const mappedSkus = addedSkus.map(({ id, qty }) => ({ id, qty }));
        dispatch(MapSkus({
          mappedSkus,
          product_id: products[rowIndex].id
        })).then(({ payload }) => {
          if (payload?.success) {
            onSave();
            onHide();
            expandRow(products[rowIndex].id);
          }
          else if (!isEmpty(payload?.errorData)) handleShowMappingAlert();
        });
      }}
      buttonDisabled={mappingSkus || !addedSkus?.length}
    >
      <InventoryWrapper className='sku-modal'>
        <Spin tip='Loading...' spinning={mappingSkus}>
          <h4 className='text-primary fw-bold mb-3'>MAP SKU to Product</h4>
          <div className="product-title mb-4">
            <img src={products[rowIndex].image_url || ProductImg} alt="product" width={28} height={28} className='product-img' />
            <div className='w-100'>
              <Tooltip placement="bottom" title={products[rowIndex].name}>
                <span className="product-name">{products[rowIndex].name}</span>
              </Tooltip>
              <div className='asin'>
                <span>{products[rowIndex].identifier_value}</span>
                <img src={copyIcon} alt='copy' className='cursor-pointer'
                  onClick={() => copyToClip(products[rowIndex].identifier_value)}
                />
              </div>
            </div>
          </div>
          <Row gutter={24} className='mb-5'>
            <Col span={11}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label
                  style={{
                    marginBottom: 4,
                    color: '#78909C',
                    fontSize: 12
                  }}
                >
                  Search SKUs
                </label>
                <AsyncSearch
                  placeholder="SKU"
                  isClearable={true}
                  isSearchable={true}
                  options={getMappedSkusList(skusList)}
                  value={selectedSku}
                  loadMoreData={handleScroll}
                  loading={fetchingSkusList}
                  onChange={(e) => {
                    if (e) {
                      setSelectedSku(e)
                    } else {
                      setSelectedSku(null);
                      setQuantity("");
                      setFilters({ search: '' });
                      dispatch(GetSkusList({
                        product_id: products[rowIndex].id,
                        page
                      }));
                    }
                  }
                  }
                  handleSearch={handleSkuSearch}
                />
              </div>
            </Col>
            <Col span={13}>
              <div className='d-flex align-items-end gap-3 add-sku-btn'>
                <Input
                  placeholder="Enter Quantity"
                  value={quantity}
                  type="number"
                  onChange={(e) => setQuantity(e.target.value)}
                  label="Pack Quantity"
                  width="80%"
                  marginBottom="0"
                />
                <Button
                  text="Add SKU"
                  className="mb-0"
                  onClick={handleAddSku}
                  disabled={!selectedSku || !quantity || quantity <= 0}
                />
              </div>
            </Col>
          </Row>
          {addedSkus.length === 0 ?
            <div className='no-data d-flex flex-column align-items-center justify-content-center'>
              <img src={noData} alt='no-data' />
              <h6>No Data</h6>
            </div>
            :
            <div className='map-height'>
              {addedSkus.map((sku, index) => (
                <div className="product-title option-product w-100 mb-2" key={index}>
                  <img src={sku.image} alt="product" width={28} height={28} className="product-img" />
                  <div className="d-flex align-items-center justify-content-center gap-3 w-100">
                    <div className="w-100">
                      <Tooltip placement="bottom" title={sku.title}>
                        <span className="product-name">{sku.title}</span>
                      </Tooltip>
                      <div className="d-flex gap-2">
                        <div className="asin">
                          <span>ASIN <span>{sku.asin}</span></span>
                          <img src={copyIcon} alt="copy" className="cursor-pointer"
                            onClick={() => copyToClip(sku.asin)} />
                        </div>
                        <div className="asin sku">
                          <span>SKU <span>{sku.seller_sku}</span></span>
                          <img src={copyIcon} alt="copy" className="cursor-pointer"
                            onClick={() => copyToClip(sku.seller_sku)} />
                        </div>
                        <div className="asin qty">
                          <span>Qty: <span>{sku.qty}</span></span>
                          <img src={copyIcon} alt="copy" className="cursor-pointer"
                            onClick={() => copyToClip(sku.qty)} />
                        </div>
                      </div>
                    </div>
                    <img
                      src={deleteIcon}
                      alt="delete"
                      className="cursor-pointer"
                      onClick={() => handleDeleteSku(index)}
                    />
                  </div>
                </div>
              ))}
            </div>
          }
        </Spin>
      </InventoryWrapper>
    </Modal>
  );
};

export default MapSkuModal;
